import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Reveal, { EASE } from "./Reveal";
import SectionHeading from "./SectionHeading";
import ArtFrame from "./ArtFrame";
import Overlay, { ActionButton } from "./Overlay";

const BlockLabel = ({ children }) => (
  <span className="font-tech text-[9px] tracking-[0.3em] text-[#D4AF37]/80 uppercase">{children}</span>
);

const Worlds = ({ worlds }) => {
  const [active, setActive] = useState(0);
  const [open, setOpen] = useState(false);
  const world = worlds[active];

  const openWorld = (i) => {
    setActive(i);
    setOpen(true);
  };

  return (
    <section id="worlds" data-testid="worlds-section" className="relative z-10 px-6 py-28 md:px-16 lg:px-24 lg:py-40">
      <SectionHeading
        title="WORLDS"
        eyebrow="From familiar lands to worlds yet to be discovered, every place within the Cosmic Dreams Universe holds its own history, secrets and stories."
      />
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
        <div className="flex flex-col gap-2 lg:col-span-4">
          {worlds.map((w, i) => (
            <Reveal key={w.id} delay={0.06 * i}>
              <button
                data-testid={`world-select-${w.id}`}
                onClick={() => openWorld(i)}
                onMouseEnter={() => setActive(i)}
                className={`group flex w-full items-baseline gap-5 border-b border-white/5 py-5 text-left transition-colors duration-500 ${
                  i === active ? "border-[#D4AF37]/40" : "hover:border-white/20"
                }`}
              >
                <span className={`font-mono text-xs tracking-[0.3em] transition-colors duration-500 ${i === active ? "text-[#D4AF37]" : "text-[#606070]"}`}>
                  {w.numeral}
                </span>
                <span>
                  <span className={`block font-serif text-xl md:text-2xl tracking-wide transition-colors duration-500 ${i === active ? "text-[#F5F5FA]" : "text-[#A0A0B0] group-hover:text-[#F5F5FA]"}`}>
                    {w.name}
                  </span>
                  <span className="mt-1 block font-tech text-[9px] text-[#606070] uppercase">{w.classification}</span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
        <div className="lg:col-span-8">
          <Reveal delay={0.15}>
            <AnimatePresence mode="wait">
              <motion.div
                key={world ? world.id : "empty"}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: EASE }}
              >
                <ArtFrame
                  image={world?.image}
                  hue={world?.hue || "blue"}
                  ratio="aspect-video"
                  label={world ? `LANDSCAPE — ${world.name}` : ""}
                  className="border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
                />
                {world && (
                  <div className="mt-6 space-y-4">
                    {world.tagline !== world.summary && (
                      <p className="font-serif text-lg md:text-xl tracking-wide text-[#D4AF37]/90">{world.tagline}</p>
                    )}
                    <p className="max-w-xl text-sm font-light leading-relaxed text-[#A0A0B0]">{world.summary}</p>
                    <div className="flex flex-col gap-4 pt-2 md:flex-row md:items-center md:justify-between">
                      <span className="font-tech text-[9px] text-[#D4AF37]/70 uppercase">{world.classification}</span>
                      <ActionButton testid="open-world-profile-btn" onClick={() => setOpen(true)}>
                        OPEN WORLD PROFILE →
                      </ActionButton>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </Reveal>
        </div>
      </div>

      <Overlay
        open={open && !!world}
        onClose={() => setOpen(false)}
        title={world?.name || ""}
        label={world ? `World Profile — ${world.classification}` : ""}
        testid="world-overlay"
      >
        {world && (
          <div className="max-w-3xl space-y-12">
            <ArtFrame
              image={world.image}
              hue={world.hue || "blue"}
              ratio="aspect-video"
              label={`LANDSCAPE — ${world.name}`}
              className="border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
            />
            <div>
              <BlockLabel>Classification</BlockLabel>
              <p className="mt-3 font-mono text-xs tracking-[0.25em] text-[#F5F5FA] uppercase">{world.classification}</p>
              {world.footnote && (
                <p className="mt-2 max-w-xl text-[11px] font-light leading-relaxed text-[#606070]">{world.footnote}</p>
              )}
            </div>
            <div>
              <BlockLabel>Short Description</BlockLabel>
              {world.tagline !== world.summary && (
                <p className="mt-3 font-serif text-xl md:text-2xl tracking-wide text-[#D4AF37]/90">{world.tagline}</p>
              )}
              <p className="mt-3 max-w-2xl text-sm md:text-base font-light leading-relaxed text-[#A0A0B0]">{world.summary}</p>
            </div>
            <div>
              <BlockLabel>World Profile</BlockLabel>
              <div className="mt-4 space-y-5">
                {world.profile?.map((p, i) => (
                  <p key={i} className="max-w-2xl text-sm md:text-base font-light leading-relaxed text-[#A0A0B0]">{p}</p>
                ))}
              </div>
            </div>
            {world.features?.length > 0 && (
              <div>
                <BlockLabel>Key Features</BlockLabel>
                <ul className="mt-4 space-y-2">
                  {world.features.map((f) => (
                    <li key={f} className="font-mono text-[11px] tracking-[0.15em] text-[#A0A0B0]">
                      <span className="mr-3 text-[#D4AF37]/60">✦</span>{f}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {world.mystery && (
              <div data-testid="world-mystery" className="border border-[#D4AF37]/20 bg-white/[0.02] p-8 md:p-10">
                <BlockLabel>{world.mystery.title}</BlockLabel>
                <div className="mt-5 space-y-3">
                  {world.mystery.lines.map((l) => (
                    <p key={l} className="font-serif text-lg md:text-xl tracking-wide text-[#F5F5FA]/90">{l}</p>
                  ))}
                </div>
                <p className="mt-6 font-mono text-[9px] tracking-[0.35em] text-[#606070] uppercase">
                  The final answer remains sealed within future stories
                </p>
              </div>
            )}
            <div>
              <BlockLabel>Story Connection</BlockLabel>
              <p className="mt-4 max-w-2xl text-sm md:text-base font-light leading-relaxed text-[#A0A0B0]">{world.story}</p>
            </div>
          </div>
        )}
      </Overlay>
    </section>
  );
};

export default Worlds;
